import { Box, Chip, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { proxied } from "../lib/imageProxy.js";
import { decodeHtml } from "../lib/displayHelpers.js";
import { NEON_GLOW_SOFT } from "./Neon.js";
import { SaveButton } from "./SaveButton.js";

export interface DirtTalkCardProps {
  variant: string;
  date: string;
  title: string;
  body: string;
  imageUrl?: string | null;
  status?: string;
  showStatus?: boolean;
  // Provide both to render a SaveButton overlay (top-right). Both required so
  // we never render an inert button without an id to act on.
  itemId?: string;
  showSaveButton?: boolean;
}

/**
 * How a Dirt Talk card looks to the public reader. Reused in the dashboard
 * preview so Drew sees the final presentation before publishing — not a
 * bare text block.
 */
export function DirtTalkCard({
  variant,
  date,
  title,
  body,
  imageUrl,
  status,
  showStatus,
  itemId,
  showSaveButton,
}: DirtTalkCardProps) {
  const cleanTitle = decodeHtml(title);
  const cleanBody = decodeHtml(body);
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "background.paper",
        borderRadius: 1,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {showSaveButton && itemId && (
        <SaveButton itemType="dirttalk" itemId={itemId} variant="overlay" />
      )}
      {imageUrl && (
        <Box
          component="img"
          src={proxied(imageUrl)}
          alt=""
          loading="lazy"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
          sx={{
            width: "100%",
            aspectRatio: "16 / 9",
            objectFit: "cover",
            display: "block",
            bgcolor: "action.hover",
          }}
        />
      )}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
          <Typography
            variant="overline"
            color="primary.main"
            sx={{
              lineHeight: 1,
              whiteSpace: "nowrap",
              textShadow: NEON_GLOW_SOFT,
              fontWeight: 700,
              letterSpacing: 1.5,
            }}
          >
            {variant.replaceAll("_", " ")}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {dayjs(date).format("D MMM YYYY")}
          </Typography>
          {showStatus && status && (
            <Chip
              size="small"
              label={status}
              color={
                status === "PENDING"
                  ? "warning"
                  : status === "APPROVED" || status === "PUBLISHED" || status === "AUTO_PUBLISHED"
                    ? "success"
                    : "default"
              }
            />
          )}
        </Stack>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, lineHeight: 1.15 }}>
          {cleanTitle}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {cleanBody}
        </Typography>
      </Box>
    </Box>
  );
}
